import React, { useEffect, useState } from 'react';
import { AlertCircle, RefreshCw, MessageSquare, PhoneCall, ArrowLeft, ShieldAlert } from 'lucide-react';
import { getLatestApplicationData } from '../utils/applicationService';

export default function PaymentFailurePage({ onRetry, onReturnHome }) {
  const [failData, setFailData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const txnId = params.get('txnid') || params.get('mihpayid') || `PAYU_${Date.now()}`;
    const name = params.get('name') ? decodeURIComponent(params.get('name')) : null;
    const email = params.get('email') ? decodeURIComponent(params.get('email')) : null;
    const phone = params.get('phone') ? decodeURIComponent(params.get('phone')) : null;
    const course = params.get('course') ? decodeURIComponent(params.get('course')) : null;

    const savedData = getLatestApplicationData();

    setFailData({
      txnId,
      fullName: name || savedData?.fullName || 'Applicant',
      email: email || savedData?.email || 'applicant@email.com',
      phone: phone || savedData?.phone || '',
      course: course || savedData?.course || 'Christ University Online Programme',
      amount: '₹500',
      date: new Date().toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'medium' })
    });
  }, []);

  if (!failData) return null;

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', padding: '2rem 1rem 5rem 1rem', color: '#0F172A' }}>
      <div className="container" style={{ maxWidth: '720px', margin: '0 auto' }}>
        
        {/* Navigation Return */}
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={onReturnHome}
            style={{
              background: 'none',
              border: 'none',
              color: '#0F2537',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.92rem'
            }}
          >
            <ArrowLeft size={18} /> Back to Homepage
          </button>

          <span style={{ fontSize: '0.82rem', color: '#DC2626', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            <ShieldAlert size={16} /> PayU Gateway Response
          </span>
        </div>

        {/* Failure Alert Banner */}
        <div style={{
          backgroundColor: '#991B1B',
          color: '#FFFFFF',
          borderRadius: '24px',
          padding: '2.5rem 2rem',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(153, 27, 27, 0.25)',
          marginBottom: '2rem'
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            backgroundColor: '#EF4444',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem auto',
            boxShadow: '0 0 25px rgba(239, 68, 68, 0.5)'
          }}>
            <AlertCircle size={44} color="#FFFFFF" />
          </div>

          <span style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: '#FEE2E2',
            fontWeight: 800,
            fontSize: '0.8rem',
            padding: '0.35rem 0.9rem',
            borderRadius: '9999px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Transaction Unsuccessful
          </span>

          <h1 style={{ fontSize: '2.1rem', fontWeight: 800, margin: '0.85rem 0 0.4rem 0', color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            Payment Could Not Be Processed
          </h1>
          
          <p style={{ fontSize: '1rem', color: '#FEE2E2', maxWidth: '540px', margin: '0 auto', lineHeight: 1.5 }}>
            We were unable to complete your transaction via PayU Gateway. No registration fee was debited from your bank account.
          </p>
        </div>

        {/* Transaction Recap Card */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          border: '1.5px solid #CBD5E1',
          padding: '1.75rem',
          boxShadow: '0 10px 25px rgba(15, 37, 55, 0.05)',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0F2537', marginBottom: '1rem', borderBottom: '1px solid #E2E8F0', paddingBottom: '0.65rem' }}>
            Transaction Failure Details
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Applicant Name:</span>
              <strong>{failData.fullName}</strong>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Mobile Number:</span>
              <strong>+91 {failData.phone}</strong>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Program Selected:</span>
              <strong style={{ color: '#0F2537' }}>{failData.course}</strong>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>PayU Transaction Ref:</span>
              <strong style={{ fontFamily: 'monospace', color: '#DC2626' }}>{failData.txnId}</strong>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Status:</span>
              <span style={{ backgroundColor: '#FEE2E2', color: '#DC2626', fontWeight: 800, padding: '0.15rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem' }}>
                FAILED / CANCELLED
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Attempt Date:</span>
              <span>{failData.date}</span>
            </div>
          </div>
        </div>

        {/* Action Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <button
            onClick={onRetry}
            className="btn-primary"
            style={{ width: '100%', padding: '0.95rem', fontSize: '1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <RefreshCw size={18} /> Retry Payment & Complete Application
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.85rem' }}>
            <a
              href="https://api.whatsapp.com/send?phone=917631900600&text=Hi,%20my%20PayU%20payment%20failed%20for%20Christ%20University%20Online%20application.%20Please%20help."
              target="_blank"
              rel="noreferrer"
              style={{
                backgroundColor: '#25D366',
                color: '#FFFFFF',
                borderRadius: '10px',
                padding: '0.85rem',
                fontSize: '0.9rem',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem'
              }}
            >
              <MessageSquare size={18} /> Support on WhatsApp
            </a>

            <a
              href="tel:+917631900600"
              style={{
                backgroundColor: '#0F2537',
                color: '#FFFFFF',
                borderRadius: '10px',
                padding: '0.85rem',
                fontSize: '0.9rem',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                border: '1px solid rgba(255, 255, 255, 0.15)'
              }}
            >
              <PhoneCall size={18} color="#D4AF37" /> Call Helpline: +91 7631 900 600
            </a>
          </div>

          <button
            onClick={onReturnHome}
            className="btn-secondary"
            style={{ width: '100%', padding: '0.85rem', fontSize: '0.9rem', marginTop: '0.5rem' }}
          >
            Return to Homepage
          </button>
        </div>

      </div>
    </div>
  );
}
